import React, { useEffect } from 'react';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import BarRoundedChart from '../../../../../components/ui/charts/BarRoundedChart';
import DonutChart from '../../../../../components/ui/charts/DonutChart';
import ProgressDonutChart from '../../../../../components/ui/charts/ProgressDonutChart';
import CurrencyText from '../../../../../components/ui/labels/CurrencyText';
import { ArrowDownCircle, ArrowUpCircle, Check, ChevronDown, ChevronUp, MinusCircle } from 'lucide-react';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import Button from '../../../../../components/ui/buttons/Button';
import ClientTransactionModal from '../../../../../components/modals/clientTransaction/ClientTransactionModal';
import CustomButton from '../../../../../components/ui/buttons/CustomButton';
import { formatCurrency } from '../../../../../utils/formatters';
import CustomCheckbox from '../../../../../components/ui/checkboxes/CustomCheckbox';
import Input from '../../../../../components/ui/inputs/Input';
import { snakecaseKeys } from '../../../../../utils/snakecaseKeys';
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf';

const TaskTab = ({ clientId, onSuccess, scope, canWrite }) => {
  const { addToast } = useToast();

  const [data, setData] = React.useState({
    tasks: [],
    plannerId: null,
    plannerName: '',
    spouseId: null,
    clientName: '',
    spouseName: '',
  });

  const [isSaving, setIsSaving] = React.useState(false);
  const [plannerTasksVisible, setPlannerTasksVisible] = React.useState(true);
  const [clientTasksVisible, setClientTasksVisible] = React.useState(true);
  const [spouseTasksVisible, setSpouseTasksVisible] = React.useState(true);
  const [newTaskPlanner, setNewTaskPlanner] = React.useState('');
  const [newTaskClient, setNewTaskClient] = React.useState('');
  const [newTaskSpouse, setNewTaskSpouse] = React.useState('');

  const handleTaskDelete = async (taskId) => {
    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao excluir tarefa');
      }

      addToast({ type: 'success', message: 'Tarefa excluída com sucesso!' });
      fetchData();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const handleTaskToggle = async (taskId) => {
    const updatedTasks = data.tasks.map((task) => {
      if (task.id === taskId) {
        return { ...task, done: !task.done };
      }
      return task;
    });

    setData((prevData) => ({ ...prevData, tasks: updatedTasks }));

    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/tasks/${taskId}/toggle`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao atualizar tarefa');
      }

      addToast({ type: 'success', message: 'Tarefa atualizada com sucesso!' });
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const handleAddTask = async (toDoId, toDoType, targetId) => {
    const newTask = toDoType === 'Admin' ? newTaskPlanner : toDoType === 'Client' ? newTaskClient : newTaskSpouse;
    console.log(`Adding task: ${newTask} to ${toDoType} with ID ${toDoId} for client ${targetId}`);

    if (!newTask.trim()) {
      addToast({
        type: 'error',
        message: 'Por favor, insira uma tarefa válida.',
      });
      return;
    }

    setIsSaving(true);

    const payload = snakecaseKeys({ name: newTask, todoId: toDoId, todoType: toDoType, clientId: targetId });
    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/tasks`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao salvar tarefa');
      }

      addToast({ type: 'success', message: 'Tarefa adicionada com sucesso!' });
      fetchData();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/tasks?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);

        setData(camelData);
      });
  };

  return (
    <div className="h-full flex flex-col gap-5">
      <div className="h-full grid grid-cols-3 gap-5">
        {/* 1 */}
        <div className="h-full bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
          <p className="text-xl text-blueDarker font-semibold">Suas tarefas {data.plannerName}</p>

          {/* Tasks open */}
          <div className="flex flex-col gap-2">
            {data.tasks.length > 0 ? (
              data.tasks
                .filter((t) => !t.done && t.todoType === 'Admin' && t.todoId === data.plannerId)
                .map((task) =>
                  canWrite ? (
                    <div key={task.id} className="flex items-center justify-between bg-blueLightest rounded-custom20">
                      <div className="flex items-center gap-2">
                        <CustomCheckbox
                          checked={task.done}
                          onChange={() => handleTaskToggle(task.id)}
                          label={task.name}
                          labelClassName="text-base text-blueDarker font-normal"
                        />
                      </div>
                    </div>
                  ) : (
                    <div key={task.id} className="flex items-center justify-between bg-blueLightest rounded-custom20 px-3 py-2">
                      <span className="text-base text-blueDarker font-normal">{task.name}</span>
                    </div>
                  )
                )
            ) : (
              <></>
            )}
          </div>

          <button
            className="flex items-center justify-between cursor-pointer mt-5"
            onClick={() => setPlannerTasksVisible(!plannerTasksVisible)}
          >
            <p className="text-base text-blueSteelDark font-semibold">Concluídas</p>
            {plannerTasksVisible ? (
              <ChevronDown size={18} className="text-blueSteelDark" />
            ) : (
              <ChevronUp size={18} className="text-blueSteelDark" />
            )}
          </button>

          {/* Tasks done */}
          <div className="flex flex-col gap-2">
            {data.tasks.length && plannerTasksVisible
              ? data.tasks
                  .filter((t) => t.done && t.todoType === 'Admin' && t.todoId === data.plannerId)
                  .map((task) => (
                    <div key={task.id} className="flex items-center justify-between bg-blueLightest rounded-custom20">
                      <div className="flex items-center gap-2">
                        {canWrite ? (
                          <CustomCheckbox
                            checked={task.done}
                            onChange={() => handleTaskToggle(task.id)}
                            label={task.name}
                            labelClassName="text-base text-grayExtraLight font-normal"
                          />
                        ) : (
                          <span className="text-base text-grayExtraLight font-normal">{task.name}</span>
                        )}
                      </div>
                      {canWrite && (
                        <button>
                          <MinusCircle
                            fill="#B76955"
                            size={22}
                            strokeWidth={2}
                            className="text-white cursor-pointer"
                            onClick={() => handleTaskDelete(task.id)}
                          />
                        </button>
                      )}
                    </div>
                  ))
              : plannerTasksVisible && <></>}
          </div>

          {canWrite && (
            <div className="mt-auto flex justify-between items-center gap-2">
              <div className="w-full">
                <Input
                  placeholder="Nova tarefa..."
                  value={newTaskPlanner}
                  onChange={(e) => setNewTaskPlanner(e.target.value)}
                />
              </div>
              <CustomButton onClick={() => handleAddTask(data.plannerId, 'Admin', clientId)}>ADICIONAR</CustomButton>
            </div>
          )}
        </div>

        {/* 2 */}
        <div className="h-full bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
          <p className="text-xl text-blueDarker font-semibold">Tarefas de {data.clientName}</p>

          {/* Tasks open */}
          <div className="flex flex-col gap-2">
            {data.tasks.length > 0 ? (
              data.tasks
                .filter((t) => !t.done && t.todoType === 'Client' && t.todoId === clientId)
                .map((task) =>
                  canWrite ? (
                    <div key={task.id} className="flex items-center justify-between bg-blueLightest rounded-custom20">
                      <div className="flex items-center gap-2">
                        <CustomCheckbox
                          checked={task.done}
                          onChange={() => handleTaskToggle(task.id)}
                          label={task.name}
                          labelClassName="text-base text-blueDarker font-normal"
                        />
                      </div>
                    </div>
                  ) : (
                    <div key={task.id} className="flex items-center justify-between bg-blueLightest rounded-custom20 px-3 py-2">
                      <span className="text-base text-blueDarker font-normal">{task.name}</span>
                    </div>
                  )
                )
            ) : (
              <></>
            )}
          </div>

          <button
            className="flex items-center justify-between cursor-pointer mt-5"
            onClick={() => setClientTasksVisible(!clientTasksVisible)}
          >
            <p className="text-base text-blueSteelDark font-semibold">Concluídas</p>
            {clientTasksVisible ? (
              <ChevronDown size={18} className="text-blueSteelDark" />
            ) : (
              <ChevronUp size={18} className="text-blueSteelDark" />
            )}
          </button>

          {/* Tasks done */}
          <div className="flex flex-col gap-2">
            {data.tasks.length && clientTasksVisible
              ? data.tasks
                  .filter((t) => t.done && t.todoType === 'Client' && t.todoId === clientId)
                  .map((task) => (
                    <div key={task.id} className="flex items-center justify-between bg-blueLightest rounded-custom20">
                      <div className="flex items-center gap-2">
                        {canWrite ? (
                          <CustomCheckbox
                            checked={task.done}
                            onChange={() => handleTaskToggle(task.id)}
                            label={task.name}
                            labelClassName="text-base text-grayExtraLight font-normal"
                          />
                        ) : (
                          <span className="text-base text-grayExtraLight font-normal">{task.name}</span>
                        )}
                      </div>
                      {canWrite && (
                        <button>
                          <MinusCircle
                            fill="#B76955"
                            size={22}
                            strokeWidth={2}
                            className="text-white cursor-pointer"
                            onClick={() => handleTaskDelete(task.id)}
                          />
                        </button>
                      )}
                    </div>
                  ))
              : clientTasksVisible && <></>}
          </div>

          {canWrite && (
            <div className="mt-auto flex justify-between items-center gap-2">
              <div className="w-full">
                <Input
                  placeholder="Nova tarefa..."
                  value={newTaskClient}
                  onChange={(e) => setNewTaskClient(e.target.value)}
                />
              </div>
              <CustomButton onClick={() => handleAddTask(clientId, 'Client', clientId)}>ADICIONAR</CustomButton>
            </div>
          )}
        </div>

        {/* 3 */}
        {data.spouseId ? (
          <div className="h-full bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
            <p className="text-xl text-blueDarker font-semibold">Tarefas de {data.spouseName}</p>

            {/* Tasks open */}
            <div className="flex flex-col gap-2">
              {data.tasks.length > 0 ? (
                data.tasks
                  .filter((t) => !t.done && t.todoType === 'Client' && t.todoId === data.spouseId)
                  .map((task) =>
                    canWrite ? (
                      <div key={task.id} className="flex items-center justify-between bg-blueLightest rounded-custom20">
                        <div className="flex items-center gap-2">
                          <CustomCheckbox
                            checked={task.done}
                            onChange={() => handleTaskToggle(task.id)}
                            label={task.name}
                            labelClassName="text-base text-blueDarker font-normal"
                          />
                        </div>
                      </div>
                    ) : (
                      <div key={task.id} className="flex items-center justify-between bg-blueLightest rounded-custom20 px-3 py-2">
                        <span className="text-base text-blueDarker font-normal">{task.name}</span>
                      </div>
                    )
                  )
              ) : (
                <></>
              )}
            </div>

            <button
              className="flex items-center justify-between cursor-pointer mt-5"
              onClick={() => setSpouseTasksVisible(!spouseTasksVisible)}
            >
              <p className="text-base text-blueSteelDark font-semibold">Concluídas</p>
              {spouseTasksVisible ? (
                <ChevronDown size={18} className="text-blueSteelDark" />
              ) : (
                <ChevronUp size={18} className="text-blueSteelDark" />
              )}
            </button>

            {/* Tasks done */}
            <div className="flex flex-col gap-2">
              {data.tasks.length && spouseTasksVisible
                ? data.tasks
                    .filter((t) => t.done && t.todoType === 'Client' && t.todoId === data.spouseId)
                    .map((task) => (
                      <div key={task.id} className="flex items-center justify-between bg-blueLightest rounded-custom20">
                        <div className="flex items-center gap-2">
                          {canWrite ? (
                            <CustomCheckbox
                              checked={task.done}
                              onChange={() => handleTaskToggle(task.id)}
                              label={task.name}
                              labelClassName="text-base text-grayExtraLight font-normal"
                            />
                          ) : (
                            <span className="text-base text-grayExtraLight font-normal">{task.name}</span>
                          )}
                        </div>
                        {canWrite && (
                          <button>
                            <MinusCircle
                              fill="#B76955"
                              size={22}
                              strokeWidth={2}
                              className="text-white cursor-pointer"
                              onClick={() => handleTaskDelete(task.id)}
                            />
                          </button>
                        )}
                      </div>
                    ))
                : spouseTasksVisible && <></>}
            </div>

            {canWrite && (
              <div className="mt-auto flex justify-between items-center gap-2">
                <div className="w-full">
                  <Input
                    placeholder="Nova tarefa..."
                    value={newTaskSpouse}
                    onChange={(e) => setNewTaskSpouse(e.target.value)}
                  />
                </div>
                <CustomButton onClick={() => handleAddTask(data.spouseId, 'Spouse', data.spouseId)}>
                  ADICIONAR
                </CustomButton>
              </div>
            )}
          </div>
        ) : (
          <div className="h-full bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
            <p className="text-center text-base text-blueSteelDark font-semibold">Não possui cônjuge vinculado</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskTab;
